import { Logger } from '@elyx-code/common-ts-utils';

const UNDO_DETECTOR_ELEMENT_ID_BASE = '--undo-redo-detector-element';

export interface IUndoableInterface {
	undo: () => void;
	redo: () => void;
	undoableStackValueIndex: number;
}

const undoableRecords: {
	[id: string]: IUndoableInterface;
} = {};

let ignoreChange: boolean = false;

function getDelectorElementId(undoStackId: string): string {
	return `${undoStackId}${UNDO_DETECTOR_ELEMENT_ID_BASE}`;
}

function getDetectorElement(undoStackId: string): HTMLDivElement {
	return document.getElementById(
		getDelectorElementId(undoStackId)
	) as HTMLDivElement;
}

function getCurrentStackIndex(undoStackId: string): number {
	const detectorElement = getDetectorElement(undoStackId);

	if (!detectorElement) {
		return -1;
	}

	const index = parseInt(detectorElement.innerHTML, 10);
	return index;
}

export function addUndoEventListener(
	entity: IUndoableInterface,
	undoStackId: string,
	parentElement: HTMLElement
) {
	let detectorElement = getDetectorElement(undoStackId);

	entity.undoableStackValueIndex = -1;

	if (!detectorElement) {
		// Create element if it doesn't exist.
		// Nested apps might attempt to create the same element when they come to life
		detectorElement = document.createElement('div' as any);
		detectorElement.id = getDelectorElementId(undoStackId);
		detectorElement.contentEditable = 'true';
		detectorElement.setAttribute('aria-hidden', 'true');
		detectorElement.tabIndex = -1;
		detectorElement.style.width = '10px';
		detectorElement.style.opacity = '0%';
		detectorElement.style.position = 'absolute';
		detectorElement.style.top = '-20px';
		detectorElement.style.right = '-20px';

		detectorElement.innerText = entity.undoableStackValueIndex.toString();

		parentElement.appendChild(detectorElement);
	}

	detectorElement.addEventListener('input', () => {
		if (ignoreChange) {
			return;
		}

		Logger.log('input event');

		const newIndex = getCurrentStackIndex(undoStackId);

		Logger.log(
			'entity.undoableStackValueIndex: ',
			entity.undoableStackValueIndex
		);
		Logger.log('newIndex: ', newIndex);

		if (newIndex < entity.undoableStackValueIndex) {
			if (!entity) {
				return;
			}

			entity.undo();
		} else if (newIndex > entity.undoableStackValueIndex) {
			if (!entity) {
				return;
			}

			entity.redo();
		}

		detectorElement.blur();
	});
}

export function removeUndoEventListener(undoStackId: string) {
	const detectorElement = getDetectorElement(undoStackId);

	if (detectorElement) {
		detectorElement.remove();
	}
}

// Rebase the stack after a new undoable change
// Means that we discard all the changes that were made after the current index
// Because we can no longer redo them, since they were overriden by the new change
// Most times, the new change will be the last item in the stack, so we just add it to the end
// But if the user undoes a change and then makes a new change, the new change will be added after the current index
function rebaseStack(
	newUndoableEntity: IUndoableInterface,
	undoStackId: string,
	history: any[],
	change: any
): void {
	Logger.log('rebaseStack');
	const currentStackIndex = getCurrentStackIndex(undoStackId) + 1;
	Logger.log('currentStackIndex: ', currentStackIndex);
	Logger.log('Before - history.length: ', history.length);
	Logger.log('Before - history: ', [...history]);
	history.splice(currentStackIndex, history.length - currentStackIndex, change);
	Logger.log('After - history.length: ', history.length);
	Logger.log('After - history: ', [...history]);

	const entity = newUndoableEntity;
	Logger.log(
		'Before - entity.undoableStackValueIndex: ',
		entity.undoableStackValueIndex
	);

	entity.undoableStackValueIndex = history.length - 1;

	Logger.log(
		'After - entity.undoableStackValueIndex: ',
		entity.undoableStackValueIndex
	);
}

export function setUndoableIndexValue(undoStackId: string, newIndex: number) {
	const detectorElement = getDetectorElement(undoStackId);

	// Set the value without triggering the input event
	detectorElement.innerText = newIndex.toString();
}

export function increaseIndexValueInInput(undoStackId: string) {
	ignoreChange = true;

	const detectorElement = getDetectorElement(undoStackId);

	const lastIndex = getCurrentStackIndex(undoStackId);
	const newIndex = lastIndex + 1;

	Logger.log('lastIndex: ', lastIndex);
	Logger.log('newIndex: ', newIndex);

	const previouslyFocusedElement = document.activeElement as HTMLElement | null;

	detectorElement.focus();
	document.execCommand('selectAll');
	document.execCommand('insertText', false, newIndex.toString());

	// Unfocus
	detectorElement.blur();

	if (
		previouslyFocusedElement &&
		previouslyFocusedElement !== detectorElement &&
		typeof previouslyFocusedElement.focus === 'function'
	) {
		previouslyFocusedElement.focus();
	}

	ignoreChange = false;
}

export function pushNewUndoState(
	entity: IUndoableInterface,
	undoStackId: string,
	history: any[],
	change: any
) {
	Logger.log('new undoable change');
	rebaseStack(entity, undoStackId, history, change);

	// if (history.length === 1 && entity.undoableStackValueIndex === 0) {
	// 	// The first change
	// } else {
	increaseIndexValueInInput(undoStackId);
	// }
}

export abstract class UndoableEntity<ValueType = any>
	implements IUndoableInterface
{
	undoValueStack: ValueType[] = [];
	undoableStackValueIndex: number = 0;
	abstract onUndo(restoredValue: ValueType, previousValue: ValueType): any;
	abstract onRedo(restoredValue: ValueType, previousValue: ValueType): any;

	constructor(initialValue: ValueType) {
		this.undoValueStack = [initialValue];
	}

	get _value(): ValueType {
		return this.undoValueStack[this.undoableStackValueIndex];
	}

	undo() {
		const previousIndex = this.undoableStackValueIndex;
		this.undoableStackValueIndex =
			this.undoableStackValueIndex > 0 ? this.undoableStackValueIndex - 1 : 0;
		this.onUndo?.(this._value, this.undoValueStack[previousIndex]);
	}

	redo() {
		if (this.undoValueStack.length) {
			const previousIndex = this.undoableStackValueIndex;
			this.undoableStackValueIndex =
				this.undoableStackValueIndex < this.undoValueStack.length - 1
					? this.undoableStackValueIndex + 1
					: this.undoValueStack.length - 1;

			this.onRedo?.(this._value, this.undoValueStack[previousIndex]);
		}
	}

	undoableChange(value: ValueType, undoStackId: string) {
		// Replace the values history items after the current index
		this.undoableStackValueIndex = this.undoableStackValueIndex + 1;
		this.undoValueStack.splice(
			this.undoableStackValueIndex,
			this.undoValueStack.length - this.undoableStackValueIndex,
			value
		);

		pushNewUndoState(this, undoStackId, this.undoValueStack, value);
	}

	nonUndoableChange(value: ValueType) {
		this.undoValueStack = [value];
		this.undoableStackValueIndex = 0;
	}

	resetUndoableStack(initialValue: ValueType) {
		this.undoValueStack = [initialValue];
		this.undoableStackValueIndex = 0;
	}

	// When sharing data in real time collaboration another user might edit this entity
	// The we replace the last element in the undo array,
	// The current user would then only encounter the value if they redo all the undos
	// Returns true if it updated the current value and false if the current value was not the last one
	replaceTip(newValue: ValueType): boolean {
		if (this.undoValueStack.length) {
			this.undoValueStack[this.undoValueStack.length - 1] = newValue;
		}

		return this.undoableStackValueIndex === this.undoValueStack.length - 1;
	}
}
